import { type BigDecimalRangeDto, type Currency, type Language, ListingSortOption } from '@ev-tech/search-modules-api';

import { formatNumber, formatPrice, validateRangeValues } from './numberFormatters';
import { SortingOptionId } from '../enums/enums';

const formatValue = (params: {
  shouldFormatNumbers?: boolean;
  language: Language;
  val: number;
  isCurrency?: boolean;
  currency?: Currency;
}): string => {
  const { shouldFormatNumbers, language, val, isCurrency, currency } = params;
  if (!val) return '';
  if (!shouldFormatNumbers) return val.toString();

  if (isCurrency) {
    return formatPrice(val, language, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      currency,
    });
  } else {
    return formatNumber({ number: val, language }, { ...(val >= 1000 && { maximumSignificantDigits: 3 }) });
  }
};

export const formatRangeLabel = (params: {
  value: BigDecimalRangeDto;
  unit: string;
  language: Language;
  shouldFormatNumbers?: boolean;
  isCurrency?: boolean;
  currency?: Currency;
}): string => {
  const { value, unit, language, shouldFormatNumbers, isCurrency, currency } = params;

  const range = validateRangeValues(value);
  const { min, max } = range;

  if (min == null && max == null) {
    return '';
  }

  let formattedMin: number | string = min ?? 0;
  let formattedMax: number | string = max ?? 0;

  if (shouldFormatNumbers) {
    formattedMin = min ? formatValue({ shouldFormatNumbers, language, val: min, isCurrency, currency }) : 0;
    formattedMax = max ? formatValue({ shouldFormatNumbers, language, val: max, isCurrency, currency }) : 0;
  }

  const unitLabel = unit && !isCurrency ? ` ${unit}` : '';

  if (min && !max) {
    return `min ${formattedMin}${unitLabel}`;
  }
  if (max && !min) {
    return `max ${formattedMax}${unitLabel}`;
  }
  return `${[formattedMin, formattedMax].filter(Boolean).join(' - ')}${unitLabel}`;
};

export const getTrackingQueryParams = (url: URL): Record<string, string> | undefined => {
  const trackingQueryParams = ['utm_campaign', 'utm_content', 'utm_medium', 'utm_source', 'utm_term', 'gclid', 'msclkid', 'fbclid'];

  const trackingParamsInUrl: Record<string, string> = {};
  for (const param of trackingQueryParams) {
    const value = url.searchParams.get(param.toLowerCase());
    if (value !== null) {
      trackingParamsInUrl[param] = value;
    }
  }
  return Object.keys(trackingParamsInUrl).length > 0 ? trackingParamsInUrl : undefined;
};

/**
 * Resolves sort field/order values. Smart Sort and AI Preferences both use
 * ListingSortOption.PREFERENCES in the API, so sortingOptionId is required to distinguish them.
 */
export const getSortValues = ({
  sortingOption,
  sortingOptionId,
}: {
  sortingOption?: ListingSortOption;
  sortingOptionId?: SortingOptionId;
} = {}) => {
  if (sortingOptionId === SortingOptionId.SMART_SORT) {
    return { sortOrder: 'DESC', sortField: SortingOptionId.SMART_SORT };
  }

  if (!sortingOption) return {};

  if (sortingOption === ListingSortOption.PREFERENCES) {
    return { sortOrder: 'DESC', sortField: ListingSortOption.PREFERENCES };
  }

  const sortArr = sortingOption.split('_');
  const sortOrder = sortArr.pop() as 'ASC' | 'DESC';
  const sortField = sortArr.join('_');

  return { sortOrder, sortField };
};
